import { createContext, Dispatch, PropsWithChildren, useContext, useReducer } from 'react'

import { StepperGroup } from 'views/AdjustersHub/ClaimView/components/Stepper/interface'

interface OneClaimState {
  localStore: LocalForage | null
  stepsState?: StepperGroup
  currentRequiredFields: string[]
  currentFields: string[]
}

const defaultStateContext = {
  localStore: null,
  stepsState: undefined,
  currentRequiredFields: [],
  currentFields: [],
}

type ActionReturns =
  | { type: 'setLocalStore'; localStore: LocalForage }
  | { type: 'setStepsState'; stepsState: StepperGroup }
  | { type: 'setCurrentRequiredFields'; currentRequiredFields: string[] }
  | { type: 'setCurrentFields'; currentFields: string[] }

type DispatchOneClaimActions = Dispatch<ActionReturns>

const OneClaimContext = createContext<OneClaimState | undefined>(undefined)
OneClaimContext.displayName = 'OneClaimContext'

const OneClaimDispatchContext = createContext<DispatchOneClaimActions | undefined>(undefined)
OneClaimDispatchContext.displayName = 'OneClaimDispatchContext'

function oneClaimReducer(state: OneClaimState, action: ActionReturns): OneClaimState {
  switch (action.type) {
    case 'setLocalStore':
      return {
        ...state,
        localStore: action.localStore,
      }
    case 'setStepsState':
      return {
        ...state,
        stepsState: action.stepsState,
      }
    case 'setCurrentRequiredFields':
      return {
        ...state,
        currentRequiredFields: action.currentRequiredFields,
      }
    case 'setCurrentFields':
      return {
        ...state,
        currentFields: action.currentFields,
      }
    default:
      return state
  }
}

function OneClaimProvider({ children }: PropsWithChildren): JSX.Element | null {
  const [state, dispatch] = useReducer(oneClaimReducer, defaultStateContext)

  return (
    <OneClaimContext.Provider value={state}>
      <OneClaimDispatchContext.Provider value={dispatch}>
        {children}
      </OneClaimDispatchContext.Provider>
    </OneClaimContext.Provider>
  )
}

const useOneClaimState = (): OneClaimState => {
  const context = useContext(OneClaimContext)
  if (context === undefined) {
    throw new Error('useOneClaimState hook must be used within a OneClaimProvider')
  }

  return context
}

const useOneClaimDispatch = (): DispatchOneClaimActions => {
  const context = useContext(OneClaimDispatchContext)

  if (context === undefined) {
    throw new Error('useOneClaimDispatch hook must be used within a OneClaimProvider')
  }

  return context
}

export { OneClaimProvider, useOneClaimState, useOneClaimDispatch, OneClaimState }
